import type { FileType } from '@unifyapps/defs/blocks/FileUpload/types';
import { produce } from 'immer';
import _set from 'lodash/set';
import { useCallback, useMemo } from 'react';
import {
  getFileTypeFromFileInProgress,
  getFileTypeFromUploadedFile,
} from '../../../../../../fields/FileUploaderField/utils';
import { useUppy } from '../../../../../../hooks/useUppy';
import { useGetDependencyGraphProvider } from '../../../../../context/DependencyGraphProvider';
import { useGetGlobalStateStoreStateByInstanceId } from '../../../../../stores/GlobalStateStore';
import { useInterfaceStore } from '../../../../../stores/InterfaceStore';
import { ComputeContextKeys } from '../../../../computeContext/const';

const CONTROL_MEDIA_UPLOADS_REFERENCE_ID = 'media-upload-state';

export const useGetControlMediaUploadsReferenceId = () => {
  const activePageId = useInterfaceStore().hooks.useActiveInterfacePageId();
  const pageScopeKey = useInterfaceStore().hooks.usePageScopeKey();
  const pageId = pageScopeKey ?? activePageId;
  return useMemo(() => `${pageId}-${CONTROL_MEDIA_UPLOADS_REFERENCE_ID}`, [pageId]);
};

export function useMediaUploadState() {
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();
  const { getDependencyGraphProvider } = useGetDependencyGraphProvider();
  const referenceId = useGetControlMediaUploadsReferenceId();

  const updateMediaUploadState = useCallback(
    (file?: FileType, instanceId?: string) => {
      if (!file) {
        return;
      }

      const blockId = file.meta?.entityId as string | undefined;
      const { mediaUploads, actions: globalStateActions } = getGlobalStateStoreState(instanceId);

      if (blockId) {
        const nextState = produce(mediaUploads, (draft) => {
          const existingFiles = mediaUploads[blockId] || [];
          const existingFileIndex = existingFiles.findIndex(
            (existingFile) => existingFile.id === file.id,
          );

          if (existingFileIndex >= 0) {
            _set(draft, `${blockId}[${existingFileIndex}]`, file);
          } else {
            _set(draft, blockId, [...existingFiles, file]);
          }
        });
        globalStateActions.setMultipleMediaUploads(nextState);
      }
      getDependencyGraphProvider(instanceId).actions.notifyDependencyChange({
        id: ComputeContextKeys.MEDIA_UPLOADS,
      });
    },
    [getGlobalStateStoreState, getDependencyGraphProvider],
  );

  const {
    uppy,
    files,
    removeFileById,
    clearAll,
    isUploading,
    updateFiles,
    retryFileById,
    retryAll,
  } = useUppy({
    referenceId,
    // Match files where referenceId starts with the page-specific prefix
    // This allows multiple blocks on the same page to share the same uppy instance
    referenceIdMatcher: useCallback((fileReferenceId: string, hookReferenceId: string) => {
      return fileReferenceId.startsWith(hookReferenceId);
    }, []),
    onUploadSuccess: async (file) => {
      updateMediaUploadState(getFileTypeFromUploadedFile(file), file.meta?.instanceId);
      await Promise.resolve(); // Ensure async function has await
      return file;
    },
    onProgress: (file) => {
      updateMediaUploadState(getFileTypeFromFileInProgress(file), file.meta?.instanceId);
    },
    onFileAdded: (file) => {
      updateMediaUploadState(getFileTypeFromFileInProgress(file), file.meta?.instanceId);
      return file;
    },
    onUploadError: (file, error) => {
      console.error('Error uploading file:', error);
      return file;
    },
  });

  const removeFileFromMediaUploadsState = useCallback(
    (fileId: string, instanceId?: string) => {
      const { mediaUploads, actions: globalStateActions } = getGlobalStateStoreState(instanceId);
      removeFileById(fileId);

      Object.entries(mediaUploads).forEach(([blockId, files]) => {
        if (files) {
          const updatedFiles = files.filter((file) => file.id !== fileId);

          if (updatedFiles.length !== files.length) {
            if (updatedFiles.length === 0) {
              globalStateActions.removeMediaUploads(blockId);
            } else {
              globalStateActions.setMultipleMediaUploads({ [blockId]: updatedFiles });
            }
          }
        }
      });

      getDependencyGraphProvider(instanceId).actions.notifyDependencyChange({
        id: ComputeContextKeys.MEDIA_UPLOADS,
      });
    },
    [getGlobalStateStoreState, getDependencyGraphProvider, removeFileById],
  );

  const removeSourceFromMediaUploadsState = useCallback(
    (sourceId: string, instanceId?: string) => {
      const { mediaUploads, actions: globalStateActions } = getGlobalStateStoreState(instanceId);
      const files = mediaUploads[sourceId];
      if (files) {
        files.forEach((file) => {
          removeFileById(file.id);
        });
      }
      globalStateActions.removeMediaUploads(sourceId);
      getDependencyGraphProvider(instanceId).actions.notifyDependencyChange({
        id: ComputeContextKeys.MEDIA_UPLOADS,
      });
    },
    [getGlobalStateStoreState, getDependencyGraphProvider, removeFileById],
  );

  const resetMediaUploads = useCallback(
    (instanceId?: string) => {
      const { mediaUploads } = getGlobalStateStoreState(instanceId);

      const allReferenceIds = Object.keys(mediaUploads);
      allReferenceIds.forEach((refId) => {
        removeSourceFromMediaUploadsState(refId, instanceId);
      });
    },
    [getGlobalStateStoreState, removeSourceFromMediaUploadsState],
  );

  return {
    uppy,
    files,
    clearAll,
    isUploading,
    updateFiles,
    removeFileFromMediaUploadsState,
    removeSourceFromMediaUploadsState,
    resetMediaUploads,
    retryFileById,
    retryAll,
  };
}
